import {defineStore, storeToRefs} from "pinia";
import axios from "axios";
import {FullReport} from "../components/ContactsEditor/schema";
import {PopulationProfile, UploadType} from "@/types";
import {
    BFilter,
    BFilterableField,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import {BSortOptions, BTableColumn, BTableOptions} from "@/components/BTable/BTable.types";
import {defaultValidationMessagesFilterableFields} from "@/tableDefaults";
import {computed, ref} from "vue";
import {useBaseStore} from "./baseStore";
import {useOriStore} from "./oriStore";
import {handleError} from "./storeHelper";

export const usePopulationStore = defineStore("populationStore", () => {
    var baseStore = useBaseStore();
    const apiUrl = "api/v1/population";
    baseStore.apiUrl.value = apiUrl;
    baseStore.dataset.value = UploadType.population;

    var oriStore = useOriStore();
    oriStore.reset();
    oriStore.dataset = UploadType.population;

    const settings = ref<PopulationProfile[]>([]);
    const loading = ref(false);

    // I'm not sure why the FullReport for contacts is in this store but I'm leaving it in case it's being used
    var contactFullReport = ref({} as FullReport | undefined);

    var getStatisticsColumns = computed(() => {
        return [
            {
                key: "type",
                text: "",
                sortable: false,
                value: (item) => item.type,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
            {
                key: "submitted",
                text: "Added",
                sortable: false,
                value: (item) => item.submitted,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
            {
                key: "rejected",
                text: "Updated",
                sortable: false,
                value: (item) => item.rejected,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
        ];
    });

    var getColumns = computed(() => {
        return [
            {
                key: "errorType",
                text: "Type",
                sortable: true,
                value: (item) => item["errorType"],
            },
            {
                key: "description",
                text: "Description",
                sortable: true,
                value: (item) => item.description,
            },
        ];
    });

    var getFilterableFields = computed(() => {
        return [
            {
                property: "errorCode",
                label: "Error Code",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.errorCodes,
            },
            {
                property: "errorType",
                label: "Type",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.getTypes,
            },
        ] as BFilterableField[];
    });

    var getValidationMessagesColumns = computed(() => {
        return [
            {
                key: "description",
                text: "Error",
                sortable: false,
                value: (item) => item.description,
                columnClass: ["text-start"],
                class: (item) => ["align-start", "text-wrap"],
            },
            {
                key: "errorCode",
                text: "Value",
                sortable: false,
                value: (item) => item.value,
                columnClass: ["text-start"],
                class: (item) => ["align-start", "text-wrap"],
            },
        ] as BTableColumn[];
    });

    var getValidationMessagesFilterableFields = computed(() => {
        return [
            {
                property: "errorCode",
                label: "Value",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "description",
                label: "Error",
                operations: textOperations,
                editorComponent: "text-editor",
            },
        ] as BFilterableField[];
    });

    var showWarningDetails = computed(() => {
        return false;
    });

    var errorCodes = computed(() => {
        return baseStore.errorCodes;
    });

    var errorDescriptions = computed(() => {
        return baseStore.errorDescriptions;
    });

    function getValidationMessagesForFile(
        id: number,
        pageNumber: number,
        pageSize: number,
        filterInfo: BFilter[],
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getValidationMessagesForFile(
            id,
            pageNumber,
            pageSize,
            filterInfo,
            filterableFields,
            sortingInfo
        );
    }

    function getRecordDetails(id: number) {
        return baseStore.getRecordDetails(id);
    }

    function getErrorsAndWarnings(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getErrorsAndWarningsBase(`${apiUrl}/errors-warnings`, tableOptions, filters, filterableFields);
    }

    function exportErrorsAndWarnings(fileType: string, filters: BFilter[], tableOptions: BTableOptions, filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        return baseStore.exportErrorsAndWarnings(fileType, tableOptions, filters, filterableFields);
    } 

    function getErrorsAndWarningsForFile(
        id: number,
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getErrorsAndWarningsForUploadDetails(id, tableOptions, filters, filterableFields);
    }

    function exportErrorsAndWarningsForFile(fileType: string, id: number, filters: BFilter[], filterableFields: BFilterableField[],sortingInfo: BSortOptions[]) {
        return baseStore.exportErrorsAndWarningsForUploadDetails(fileType, id, filters, filterableFields, sortingInfo)
    }


    function getErrorsAndWarningsColumns(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        let ewColumns = baseStore.getErrorsAndWarningsColumns(
            onlyHasOneOri,
            showAdditionalColumns,
            onlyIncludePrimaryColumns
        );

        ewColumns = ewColumns.filter((col: BTableColumn) => col.key != "incidentNumber" && col.key != "errorCode");

        return ewColumns;
    }

    function getFilterableFieldsForErrorsAndWarnings(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        return [] as BFilterableField[];
    }

    function getFiles(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getFiles(tableOptions, filters ?? [], filterableFields);
    }
    var getFileUploadColumns = baseStore.getFileUploadColumns;
    function getFilterableFieldsForFileUploads(onlyHasOneOri: boolean, submissionPeriodHidden: boolean) {
        var filterableFields = baseStore.getFilterableFieldsForFileUploads(onlyHasOneOri, submissionPeriodHidden);

        var agencyFilter = filterableFields.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return filterableFields;
    }
    var errorDescriptionsDownloadUrl = ref(baseStore.errorDescriptionsDownloadUrl);

    //ori related functions & computed properties
    const {
        oris,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        getSelectedOri,
    } = storeToRefs(oriStore);
    var fetchDatasetDetailsForOri = oriStore.fetchDatasetDetailsForOri;
    var reset = oriStore.reset;
    var setSelectedOri = oriStore.setSelectedOri;

    const listSettings = async () => {
        try {
            loading.value = true;
            const response = await axios.get<PopulationProfile[]>(`${baseStore.apiUrl.value}/settings`);
            settings.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };
    var checkIfDownloadValid = baseStore.checkIfDownloadValid;
    return {
        loading,
        settings,
        listSettings,
        contactFullReport,
        getColumns,
        getStatisticsColumns,
        getFilterableFields,
        getValidationMessagesColumns,
        getValidationMessagesFilterableFields,
        showWarningDetails,
        errorCodes,
        errorDescriptions,
        getValidationMessagesForFile,
        getRecordDetails,
        getErrorsAndWarnings,
        getFiles,
        getErrorsAndWarningsForFile,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        fetchDatasetDetailsForOri,
        reset,
        getSelectedOri,
        setSelectedOri,
        errorDescriptionsDownloadUrl,
        getFileUploadColumns,
        getFilterableFieldsForFileUploads,
        getErrorsAndWarningsColumns,
        getFilterableFieldsForErrorsAndWarnings,
        oris,
        checkIfDownloadValid,
        apiUrl,
        exportErrorsAndWarnings,
        exportErrorsAndWarningsForFile
    };
});
